/**
 * 游戏管理器 (GameManager)
 * 协调 GameEngine、AI 机器人、JudgeEngine、音效和 UI 状态
 */
import { GameEngine } from './GameEngine';
import { BotAgent } from './BotAgent';
import {
  Card,
  Player,
  GameState,
  ActionType,
  AvailableActions,
  ChiOption,
  WinResult,
  GameConfig
} from './types';
import { JudgeEngine } from './JudgeEngine';
import { Rules } from './Rules';
import { soundManager } from '../utils/sound';
import { resolveRuleConfig, RuleConfig } from './rules.config';

export class GameManager {
  private engine: GameEngine;
  private botAgents: Map<string, BotAgent> = new Map();
  private listeners: ((event: any) => void)[] = [];
  private humanAvailableActions: AvailableActions | null = null;
  private actionTimeout: any = null;
  private ruleConfig: RuleConfig;

  constructor(config: GameConfig) {
    this.ruleConfig = resolveRuleConfig({ ...config.rules, minHuxiToWin: config.minHuxiToWin });
    this.engine = new GameEngine(config);
    this.initBotAgents();
  }

  private initBotAgents(): void {
    this.botAgents.clear();
    this.engine.getPlayers().forEach(p => {
      if (p.isBot) {
        this.botAgents.set(p.id, new BotAgent(p, p.botDifficulty));
      }
    });
  }

  public subscribe(listener: (event: any) => void): void {
    this.listeners.push(listener);
  }

  public updateConfig(config: GameConfig): void {
    this.ruleConfig = resolveRuleConfig({ ...config.rules, minHuxiToWin: config.minHuxiToWin });
    this.engine.updateConfig(config);
    this.botAgents.forEach((agent, playerId) => {
      const player = this.engine.getPlayer(playerId);
      if (player) agent.setDifficulty(config.botDifficulty);
    });
  }

  private emit(event: any): void {
    this.listeners.forEach(l => l(event));
  }

  /**
   * 开始新游戏 / 新局
   */
  public startMatch(): void {
    this.engine.resetMatch();
    this.initBotAgents();
    this.startRound();
  }

  public startRound(): void {
    if (this.actionTimeout) clearTimeout(this.actionTimeout);
    this.humanAvailableActions = null;

    soundManager.playDraw();
    this.engine.startNewRound();
    this.emitState();

    // 检查庄家出牌或摸牌流程
    this.checkCurrentTurn();
  }

  /**
   * 检查当前轮次并驱动流程
   */
  private checkCurrentTurn(): void {
    const state = this.engine.getGameState();
    if (state === GameState.ROUND_OVER || state === GameState.MATCH_OVER) {
      return;
    }

    const currentTurnPlayer = this.engine.getCurrentPlayer();

    // 如果轮到真人
    if (!currentTurnPlayer.isBot) {
      if (state === GameState.WAITING_DRAW) {
        soundManager.playTurnAlert();
        if (this.actionTimeout) clearTimeout(this.actionTimeout);
        const playerId = currentTurnPlayer.id;
        this.actionTimeout = setTimeout(() => {
          this.actionTimeout = null;
          const activePlayer = this.engine.getCurrentPlayer();
          if (this.engine.getGameState() === GameState.WAITING_DRAW && activePlayer.id === playerId) {
            this.executeDraw(playerId);
          }
        }, 300);
        return;
      }
      if (state === GameState.WAITING_DISCARD) {
        soundManager.playTurnAlert();
      }
      return;
    }

    // 如果当前轮到机器人操作 (稍微延迟模拟思考过程)
    if (currentTurnPlayer.isBot) {
      if (state === GameState.WAITING_DISCARD || state === GameState.WAITING_DRAW) {
        setTimeout(() => {
          this.runBotTurn(currentTurnPlayer.id);
        }, 700);
      }
    }
  }

  /**
   * 机器人出牌或摸牌流程
   */
  private async runBotTurn(botId: string): Promise<void> {
    const player = this.engine.getPlayer(botId);
    const agent = this.botAgents.get(botId);
    if (!player || !agent) return;

    // 收集场上已出的牌给AI
    const allDiscards = this.engine.getPlayers().flatMap(p => p.discards);
    agent.updateDiscards(allDiscards);

    const state = this.engine.getGameState();

    // 1. 如果机器人处于出牌阶段 (如起手庄家21张，或吃碰扫提后21张)
    if (state === GameState.WAITING_DISCARD) {
      const discardCard = await agent.decideDiscardCard();
      if (discardCard) {
        this.executeDiscard(botId, discardCard.id);
      }
      return;
    }

    // 2. 如果机器人处于摸牌阶段
    if (state === GameState.WAITING_DRAW) {
      soundManager.playDraw();
      const drawn = this.engine.drawCard(botId);
      this.emitState();

      if (!drawn) {
        // 牌堆摸空，流局
        soundManager.playDrawGame();
        this.emitState();
        return;
      }

      soundManager.speakCard(drawn, true);
      await this.processPublicCard(drawn, true, botId);
    }
  }

  /**
   * 处理进入判定区的公共卡牌 (摸出的牌 或 打出的牌)
   */
  private async processPublicCard(card: Card, isDrawn: boolean, actorId: string): Promise<void> {
    // 新的判定区牌开始处理时，上一张牌留下的真人操作状态必须失效。
    this.humanAvailableActions = null;
    const players = this.engine.getPlayers();
    const actorIndex = players.findIndex(p => p.id === actorId);
    const nextPlayerIndex = (actorIndex + 1) % players.length;

    // 1. 检查私有强制动作 (摸牌者 提 / 扫)
    if (isDrawn) {
      const actor = players[actorIndex];
      if (Rules.canTiFromSao(actor.melds, card)) {
        soundManager.playTiPao('提');
        this.engine.executeTi(actorId, card);
        this.triggerBanter(actorId, 'ti');
        this.emitState();
        if (!this.checkWinAfterAction(actorId, ActionType.TI, true)) {
          this.checkCurrentTurn();
        }
        return;
      }
      if (Rules.canTi(actor.hand, card)) {
        soundManager.playTiPao('提');
        this.engine.executeTi(actorId, card);
        this.triggerBanter(actorId, 'ti');
        this.emitState();
        if (!this.checkWinAfterAction(actorId, ActionType.TI, true)) {
          this.checkCurrentTurn();
        }
        return;
      }
      if (Rules.canSao(actor.hand, card)) {
        soundManager.playTiPao('扫');
        this.engine.executeSao(actorId, card);
        this.triggerBanter(actorId, 'pao');
        this.emitState();
        if (!this.checkWinAfterAction(actorId, ActionType.SAO, true)) {
          this.checkCurrentTurn();
        }
        return;
      }
    }

    // 2. 检查跑 (优先级 1)
    for (const p of players) {
      if (Rules.canPao(p.hand, p.melds, card, isDrawn, p.id === actorId)) {
        soundManager.playTiPao('跑');
        this.engine.executePao(p.id, card);
        this.triggerBanter(p.id, 'pao');
        this.emitState();
        if (!this.checkWinAfterAction(p.id, ActionType.PAO, isDrawn)) {
          this.checkCurrentTurn();
        }
        return;
      }
    }

    // 3. 检查抢胡 (优先级 2)
    const winners = JudgeEngine.findWinningPlayers(players, actorIndex, card, isDrawn, this.ruleConfig.minHuxiToWin, this.ruleConfig);

    // 4. 检查人类玩家 (player_0) 的操作意向
    const human = players[0];
    const isHumanActor = human.id === actorId;
    const isHumanNext = players[nextPlayerIndex].id === human.id;
    const humanActions = JudgeEngine.evaluatePlayerActions(
      human,
      card,
      isDrawn,
      isHumanActor,
      isHumanNext,
      this.ruleConfig.minHuxiToWin,
      this.ruleConfig
    );

    // 如果真人有操作 (可以胡、碰、吃)
    const hasMeaningfulAction = humanActions.canHu || humanActions.canPeng || (humanActions.canChi && humanActions.chiOptions.length > 0);

    if (hasMeaningfulAction) {
      this.humanAvailableActions = humanActions;
      this.engine.setGameState(GameState.WAITING_ACTIONS);
      this.emitState();
      return; // 暂停流程，等待真人点击按钮响应
    }

    // 如果真人没有操作，轮询 AI 机器人的响应
    await this.resolveBotActions(card, isDrawn, actorId, winners);
  }

  /**
   * 轮询 AI 机器人对判定区卡牌的操作
   */
  private async resolveBotActions(
    card: Card,
    isDrawn: boolean,
    actorId: string,
    winners: { player: Player; winResult: WinResult }[]
  ): Promise<void> {
    const players = this.engine.getPlayers();
    const actorIndex = players.findIndex(p => p.id === actorId);

    // 1. 如果有机器人可以胡牌，按座位优先判定胡牌！
    for (const w of winners) {
      if (w.player.isBot) {
        soundManager.playWin();
        soundManager.playCoinReward();
        this.engine.handleWin(w.player.id, w.winResult);
        this.triggerBanter(w.player.id, 'win');
        this.emitState();
        return;
      }
    }

    // 2. 检查机器人碰牌 (优先级 3)
    for (let i = 1; i < players.length; i++) {
      const pIdx = (actorIndex + i) % players.length;
      const bot = players[pIdx];
      if (bot.isBot && bot.id !== actorId) {
        const agent = this.botAgents.get(bot.id);
        if (agent && Rules.canPeng(bot.hand, card)) {
          const decision = await agent.decideAction(card, {
            canHu: false,
            canTi: false,
            canPao: false,
            canSao: false,
            canPeng: true,
            canChi: false,
            chiOptions: [],
            canPass: true
          });

          if (decision.action === ActionType.PENG) {
            soundManager.playPeng();
            this.engine.executePeng(bot.id, card);
            this.triggerBanter(bot.id, 'peng');
            this.emitState();
            if (this.checkWinAfterAction(bot.id, ActionType.PENG, isDrawn, false)) return;
            this.checkCurrentTurn();
            return;
          }
        }
      }
    }

    // 3. 检查下家机器人吃牌 (优先级 4)
    const nextPlayer = players[(actorIndex + 1) % players.length];
    if (nextPlayer.isBot && nextPlayer.id !== actorId) {
      const chiOptions = Rules.getChiOptions(nextPlayer.hand, card, this.ruleConfig);
      if (chiOptions.length > 0) {
        const agent = this.botAgents.get(nextPlayer.id);
        if (agent) {
          const decision = await agent.decideAction(card, {
            canHu: false,
            canTi: false,
            canPao: false,
            canSao: false,
            canPeng: false,
            canChi: true,
            chiOptions,
            canPass: true
          });

          if (decision.action === ActionType.CHI && decision.chiOption) {
            soundManager.playChi();
            this.engine.executeChi(nextPlayer.id, decision.chiOption);
            this.triggerBanter(nextPlayer.id, 'chi');
            this.emitState();
            if (this.checkWinAfterAction(nextPlayer.id, ActionType.CHI, isDrawn, false)) return;
            this.checkCurrentTurn();
            return;
          }
        }
      }
    }

    // 4. 谁都不要 -> 判定区牌变为臭牌，进入弃牌池，轮到下家摸牌
    this.engine.passPublicCard();
    this.emitState();
    this.checkCurrentTurn();
  }

  /**
   * 真人玩家执行出牌
   */
  public executeDiscard(playerId: string, cardId: string): void {
    soundManager.playDiscard();
    const discarded = this.engine.discardCard(playerId, cardId);
    this.humanAvailableActions = null;
    this.emitState();

    if (discarded) {
      soundManager.speakCard(discarded, false);
      this.processPublicCard(discarded, false, playerId);
    }
  }

  /**
   * 真人玩家执行摸牌
   */
  public executeDraw(playerId: string): void {
    soundManager.playDraw();
    const drawn = this.engine.drawCard(playerId);
    this.humanAvailableActions = null;
    this.emitState();

    if (drawn) {
      soundManager.speakCard(drawn, true);
      this.processPublicCard(drawn, true, playerId);
    } else {
      soundManager.playDrawGame();
      this.emitState();
    }
  }

  /**
   * 真人玩家选择响应动作 (胡 / 碰 / 吃 / 过)
   */
  public async handleHumanAction(action: ActionType, chiOption?: ChiOption): Promise<void> {
    const card = this.engine.getPublicCard();
    if (!card) return;

    this.humanAvailableActions = null;
    const human = this.engine.getPlayers()[0];
    const isDrawn = this.engine.isPublicDrawn();
    const currentActorId = this.engine.getCurrentPlayer().id;

    if (action === ActionType.HU) {
      // 尝试胡牌
      const winResult = Rules.calculateWin(
        human.hand,
        human.melds,
        card,
        isDrawn && human.id === currentActorId,
        this.ruleConfig.minHuxiToWin,
        this.ruleConfig
      ) || Rules.calculatePengWin(human.hand, human.melds, card, this.ruleConfig.minHuxiToWin, this.ruleConfig);
      if (winResult && winResult.isWin) {
        soundManager.playWin();
        soundManager.playCoinReward();
        this.engine.handleWin(human.id, winResult);
        this.emitState();
        return;
      }
    } else if (action === ActionType.PENG) {
      const pengWin = Rules.calculatePengWin(human.hand, human.melds, card, this.ruleConfig.minHuxiToWin, this.ruleConfig);
      if (pengWin) {
        soundManager.playWin();
        soundManager.playCoinReward();
        this.engine.handleWin(human.id, pengWin);
        this.emitState();
        return;
      }
      soundManager.playPeng();
      this.engine.executePeng(human.id, card);
      this.emitState();
      if (!this.checkWinAfterAction(human.id, ActionType.PENG, isDrawn, false)) {
        this.advanceAfterMeld(human.id);
      }
      return; // 碰牌后轮到真人出牌
    } else if (action === ActionType.CHI && chiOption) {
      soundManager.playChi();
      this.engine.executeChi(human.id, chiOption);
      this.emitState();
      if (!this.checkWinAfterAction(human.id, ActionType.CHI, isDrawn, false)) {
        this.advanceAfterMeld(human.id);
      }
      return; // 吃牌后轮到真人出牌
    } else if (action === ActionType.PASS) {
      soundManager.playPass();
      // 真人放弃，继续由后续 AI 判定
      const winners = JudgeEngine.findWinningPlayers(
        this.engine.getPlayers(),
        this.engine.getCurrentTurnIndex(),
        card,
        isDrawn,
        this.ruleConfig.minHuxiToWin,
        this.ruleConfig
      );
      await this.resolveBotActions(card, isDrawn, currentActorId, winners);
    }
  }

  /** 吃/碰后没有可出的手牌时，直接进入下一位摸牌，避免卡在等待出牌。 */
  private advanceAfterMeld(playerId: string): void {
    const player = this.engine.getPlayer(playerId);
    if (!player || player.hand.length > 0) return;

    this.engine.nextTurn();
    this.emitState();
    this.checkCurrentTurn();
  }

  /**
   * 动作完成后检查是否胡牌
   */
  private checkWinAfterAction(
    playerId: string,
    action: ActionType,
    isDrawn: boolean,
    advanceTurnOnFailure: boolean = true
  ): boolean {
    const player = this.engine.getPlayer(playerId);
    if (!player) return false;

    // 跑牌胡只允许本局第一次跑；后续跑仍可正常完成，但不能跑牌胡。
    if (action === ActionType.PAO && player.melds.filter(meld => meld.type === 'PAO').length > 1) {
      if (advanceTurnOnFailure) {
        this.engine.nextTurn();
        this.emitState();
      }
      return false;
    }

    const winResult = Rules.calculateWin(
      player.hand,
      player.melds,
      null,
      isDrawn && player.id === this.engine.getCurrentPlayer().id,
      this.ruleConfig.minHuxiToWin,
      this.ruleConfig
    );
    if (winResult && winResult.isWin) {
      soundManager.playWin();
      soundManager.playCoinReward();
      this.engine.handleWin(playerId, winResult);
      this.emitState();
      return true;
    }

    const requiresDiscard = action === ActionType.PAO
      || action === ActionType.SAO
      || action === ActionType.PENG
      || action === ActionType.CHI;

    if (advanceTurnOnFailure && !requiresDiscard) {
      // 提完成后不出牌，直接进入下一家的摸牌阶段。
      this.engine.nextTurn();
      this.emitState();
    }
    return false;
  }

  /**
   * 触发机器人方言骚话气泡
   */
  private triggerBanter(botId: string, action: 'win' | 'ti' | 'pao' | 'peng' | 'chi' | 'disappointed'): void {
    const agent = this.botAgents.get(botId);
    const player = this.engine.getPlayer(botId);
    if (agent && player) {
      const text = agent.getBanter(action);
      this.emit({
        type: 'BANTER',
        data: { speaker: player.name, text, botId }
      });
    }
  }

  /**
   * 广播完整状态给 UI
   */
  public emitState(): void {
    this.emit({
      type: 'STATE_CHANGED',
      data: {
        gameState: this.engine.getGameState(),
        players: this.engine.getPlayers(),
        publicCard: this.engine.getPublicCard(),
        isPublicCardDrawn: this.engine.isPublicDrawn(),
        currentTurnPlayer: this.engine.getCurrentPlayer(),
        dealerPlayer: this.engine.getDealer(),
        deckSize: this.engine.getDeckSize(),
        currentRound: this.engine.getCurrentRound(),
        totalRounds: this.engine.getTotalRounds(),
        winResult: this.engine.getWinResult(),
        humanActions: this.humanAvailableActions
      }
    });
  }

  public getEngine(): GameEngine {
    return this.engine;
  }
}
